import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Building2,
  ChevronRight,
  History,
  Loader2,
  MapPin,
  Phone,
  Save,
  Truck,
  User,
  UserPlus,
  X,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { aed, money2 } from "@/lib/format";
import { cn } from "@/lib/utils";

const STATUS_COLORS = {
  preparing: "bg-amber-100 text-amber-900 border-amber-400",
  dispatched: "bg-blue-100 text-blue-900 border-blue-400",
  delivered: "bg-emerald-100 text-emerald-900 border-emerald-400",
  cancelled: "bg-red-100 text-red-900 border-red-400",
};

function normalizeContact(value) {
  return String(value || "").replace(/\D+/g, "");
}

function useDeliveryForm(bill, branchId) {
  const [form, setForm] = useState({
    customer_name: "",
    delivery_contact: "",
    delivery_building_no: "",
    delivery_flat_no: "",
    delivery_address: "",
    delivery_area_id: "",
    delivery_person_id: "",
  });
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (!bill) return;
    setForm({
      customer_name: bill.customer_name || "",
      delivery_contact: bill.delivery_contact || "",
      delivery_building_no: bill.delivery_building_no || "",
      delivery_flat_no: bill.delivery_flat_no || "",
      delivery_address: bill.delivery_address || "",
      delivery_area_id: bill.delivery_area_id ? String(bill.delivery_area_id) : "",
      delivery_person_id: bill.delivery_person_id ? String(bill.delivery_person_id) : "",
    });
  }, [
    bill?.id,
    bill?.customer_name,
    bill?.delivery_contact,
    bill?.delivery_building_no,
    bill?.delivery_flat_no,
    bill?.delivery_address,
    bill?.delivery_area_id,
    bill?.delivery_person_id,
  ]);

  const contactDigits = normalizeContact(form.delivery_contact);

  const { data: areas = [], isLoading: areasLoading } = useQuery({
    queryKey: ["delivery-areas", branchId],
    queryFn: async () => {
      const { data } = await api.get("/delivery-areas", { params: { branch_id: branchId } });
      return data.delivery_areas || [];
    },
    enabled: Boolean(branchId),
  });

  const { data: driversData, isLoading: driversLoading } = useQuery({
    queryKey: ["delivery-persons-available", branchId],
    queryFn: async () => {
      const { data } = await api.get("/delivery-persons/available", { params: { branch_id: branchId } });
      return data;
    },
    enabled: Boolean(branchId),
  });

  const drivers = driversData?.delivery_persons || [];
  const autoAssignEnabled = driversData?.auto_assign_driver ?? bill?.auto_assign_driver;

  const { data: savedLocations = [], isFetching: historyLoading } = useQuery({
    queryKey: ["delivery-locations", branchId, contactDigits],
    queryFn: async () => {
      const { data } = await api.get("/delivery-locations", {
        params: { branch_id: branchId, contact: contactDigits },
      });
      return data.delivery_locations || [];
    },
    enabled: Boolean(branchId) && contactDigits.length >= 4,
  });

  const activeAreas = areas.filter((a) => a.is_active !== false && a.status !== "Inactive");
  const suggestedDriver = bill?.driver_suggestion || drivers.find((d) => d.suggested);
  const assignedDriver = bill?.delivery_person_name;

  const selectedArea = useMemo(
    () => activeAreas.find((a) => a.id === Number(form.delivery_area_id)),
    [activeAreas, form.delivery_area_id],
  );

  const chargePreview = bill?.delivery_charge_preview;
  const localCharge = selectedArea ? Number(selectedArea.delivery_charge || 0) : 0;
  const localMin = selectedArea ? Number(selectedArea.min_order_value || 0) : 0;
  const billSubtotal = Number(bill?.subtotal || 0);
  const meetsMin = localMin <= 0 || billSubtotal >= localMin;

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const applyLocation = (loc) => {
    setForm((prev) => ({
      ...prev,
      customer_name: loc.customer_name || prev.customer_name,
      delivery_contact: loc.contact_no || prev.delivery_contact,
      delivery_building_no: loc.building_no || "",
      delivery_flat_no: loc.flat_no || "",
      delivery_address: loc.address_line || "",
      delivery_area_id: loc.delivery_area_id ? String(loc.delivery_area_id) : prev.delivery_area_id,
    }));
    setShowHistory(false);
  };

  const hasAddress =
    form.delivery_address.trim() ||
    (form.delivery_building_no.trim() && form.delivery_flat_no.trim());

  const buildPayload = () => {
    const areaId = form.delivery_area_id ? Number(form.delivery_area_id) : null;
    const area = activeAreas.find((a) => a.id === areaId);
    const payload = {
      customer_name: form.customer_name.trim() || "Walk-in",
      delivery_contact: form.delivery_contact.trim() || null,
      delivery_building_no: form.delivery_building_no.trim() || null,
      delivery_flat_no: form.delivery_flat_no.trim() || null,
      delivery_address: form.delivery_address.trim() || null,
      delivery_area_id: areaId,
      delivery_person_id: form.delivery_person_id ? Number(form.delivery_person_id) : null,
    };
    if (area) {
      payload.parcel_charge = Number(area.delivery_charge || 0);
    }
    return payload;
  };

  const status = bill?.delivery_status;
  const statusLabel = bill?.delivery_status_label || status;
  const incomplete = !form.delivery_contact.trim() || !form.delivery_area_id || !hasAddress;

  const summaryLine = [
    form.customer_name?.trim() && form.customer_name !== "Walk-in" ? form.customer_name.trim() : null,
    form.delivery_contact.trim() || null,
    selectedArea?.name || null,
    assignedDriver || null,
  ].filter(Boolean).join(" · ");

  return {
    form,
    setField,
    showHistory,
    setShowHistory,
    contactDigits,
    areasLoading,
    driversLoading,
    drivers,
    autoAssignEnabled,
    savedLocations,
    historyLoading,
    activeAreas,
    suggestedDriver,
    assignedDriver,
    selectedArea,
    chargePreview,
    localCharge,
    localMin,
    billSubtotal,
    meetsMin,
    applyLocation,
    hasAddress,
    buildPayload,
    status,
    statusLabel,
    incomplete,
    summaryLine,
  };
}

export default function DeliveryPanel({
  bill,
  branchId,
  onSave,
  isSaving,
  onAssignDriver,
  onAutoAssign,
  isAssigningDriver,
  variant = "dialog",
  onOpenDetails,
  onClose,
}) {
  const d = useDeliveryForm(bill, branchId);

  const handleSave = () => {
    onSave(d.buildPayload());
  };

  if (variant === "compact") {
    return (
      <div
        className="flex-shrink-0 border-b border-violet-200 bg-violet-50/80 px-3 py-2"
        data-testid="pos-delivery-summary"
      >
        <button
          type="button"
          onClick={onOpenDetails}
          className="w-full flex items-center gap-2 min-h-[44px] text-left touch-manipulation rounded-lg hover:bg-violet-100/80 px-1 -mx-1 transition-colors"
          data-testid="pos-delivery-open"
        >
          <span className="h-9 w-9 rounded-lg bg-violet-600 text-white grid place-items-center shrink-0">
            <Truck size={16} />
          </span>
          <span className="flex-1 min-w-0">
            <span className="text-[10px] font-bold uppercase tracking-wider text-violet-800 block">
              Delivery
            </span>
            <span className={cn(
              "text-[12px] font-semibold truncate block",
              d.summaryLine ? "text-brand-brown" : "text-red-600",
            )}>
              {d.summaryLine || "Tap to enter phone, area & address"}
            </span>
          </span>
          {d.incomplete ? (
            <span className="text-[9px] font-bold uppercase px-2 py-1 rounded-full bg-red-100 text-red-700 border border-red-200 shrink-0">
              Incomplete
            </span>
          ) : d.status ? (
            <span className={cn(
              "text-[9px] font-bold px-2 py-1 rounded-full border uppercase shrink-0",
              STATUS_COLORS[d.status] || "bg-slate-100 text-slate-700 border-slate-300",
            )}>
              {d.statusLabel}
            </span>
          ) : null}
          <ChevronRight size={16} className="text-violet-500 shrink-0" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col max-h-[min(90vh,720px)]" data-testid="pos-delivery-panel">
      <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-[#F0E0D4] bg-[#FFFCFA] shrink-0">
        <div>
          <h2 className="text-[15px] font-extrabold text-brand-brown flex items-center gap-2">
            <Truck size={18} className="text-violet-600" />
            Delivery details
          </h2>
          <p className="text-[11px] text-brand-brown/50 mt-0.5">
            Customer, address, area, and driver assignment
          </p>
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="h-9 w-9 rounded-lg hover:bg-[#FFF3E8] grid place-items-center text-brand-brown/40"
            aria-label="Close delivery details"
            data-testid="pos-delivery-close"
          >
            <X size={18} />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 min-h-0">
        <div className="flex flex-wrap gap-2">
          {d.status && (
            <span className={cn(
              "text-[10px] font-bold px-2 py-1 rounded border uppercase",
              STATUS_COLORS[d.status] || "bg-slate-100 text-slate-700 border-slate-300",
            )}>
              {d.statusLabel}
            </span>
          )}
          {d.selectedArea && (
            <span className="text-[10px] font-semibold text-sky-800 bg-sky-50 border border-sky-200 px-2 py-1 rounded">
              Charge: {aed(d.localCharge)}
              {d.localMin > 0 && ` · Min ${aed(d.localMin)}`}
            </span>
          )}
          {d.selectedArea && d.localMin > 0 && !d.meetsMin && (
            <span className="text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded">
              Add {aed(d.localMin - d.billSubtotal)} more for {d.selectedArea.name}
            </span>
          )}
          {d.chargePreview && !d.chargePreview.meets_min_order && (
            <span className="text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded">
              Short {aed(d.chargePreview.shortfall)} for minimum order
            </span>
          )}
          {d.incomplete && (
            <span className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-200 px-2 py-1 rounded">
              Phone, area &amp; address required
            </span>
          )}
        </div>

        <div className="space-y-1.5">
          <Label className="text-[11px] text-brand-brown/60">Customer name</Label>
          <div className="relative">
            <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-violet-500" />
            <Input
              value={d.form.customer_name}
              onChange={(e) => d.setField("customer_name", e.target.value)}
              placeholder="Customer name"
              className="h-10 pl-9 text-[13px]"
              data-testid="pos-delivery-customer"
            />
          </div>
        </div>

        <div className="space-y-1.5 relative">
          <Label className="text-[11px] text-brand-brown/60">Phone *</Label>
          <div className="relative">
            <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-violet-500" />
            <Input
              value={d.form.delivery_contact}
              onChange={(e) => {
                d.setField("delivery_contact", e.target.value);
                d.setShowHistory(true);
              }}
              onFocus={() => d.setShowHistory(true)}
              placeholder="Mobile number"
              className="h-10 pl-9 text-[13px]"
              data-testid="pos-delivery-phone"
            />
          </div>
          {d.showHistory && d.contactDigits.length >= 4 && (
            <div className="mt-1 bg-white border border-violet-200 rounded-lg shadow-lg max-h-36 overflow-y-auto">
              {d.historyLoading && (
                <div className="px-3 py-2 text-[11px] text-slate-500 flex items-center gap-1">
                  <Loader2 size={12} className="animate-spin" /> Searching saved addresses…
                </div>
              )}
              {!d.historyLoading && d.savedLocations.length === 0 && (
                <div className="px-3 py-2 text-[11px] text-slate-500">No saved addresses for this number</div>
              )}
              {d.savedLocations.map((loc) => (
                <button
                  key={loc.id}
                  type="button"
                  onClick={() => d.applyLocation(loc)}
                  className="w-full text-left px-3 py-2 hover:bg-violet-50 border-b border-slate-100 last:border-0"
                  data-testid={`pos-delivery-history-${loc.id}`}
                >
                  <div className="text-[12px] font-bold text-slate-900 flex items-center gap-1">
                    <History size={12} className="text-violet-600" />
                    {loc.formatted_address || loc.address_line || "Address"}
                  </div>
                  <div className="text-[10px] text-slate-600">
                    {loc.delivery_area_name || "No area"}
                    {loc.delivery_charge > 0 ? ` · ${aed(loc.delivery_charge)}` : ""}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-[11px] text-brand-brown/60">Building *</Label>
            <div className="relative">
              <Building2 size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-violet-500" />
              <Input
                value={d.form.delivery_building_no}
                onChange={(e) => d.setField("delivery_building_no", e.target.value)}
                placeholder="Bldg no."
                className="h-10 pl-9 text-[13px]"
                data-testid="pos-delivery-building"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-[11px] text-brand-brown/60">Flat *</Label>
            <Input
              value={d.form.delivery_flat_no}
              onChange={(e) => d.setField("delivery_flat_no", e.target.value)}
              placeholder="Flat / villa"
              className="h-10 text-[13px]"
              data-testid="pos-delivery-flat"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-[11px] text-brand-brown/60">Landmark / street</Label>
          <div className="relative">
            <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-violet-500" />
            <Input
              value={d.form.delivery_address}
              onChange={(e) => d.setField("delivery_address", e.target.value)}
              placeholder="Or use building + flat above"
              className="h-10 pl-9 text-[13px]"
              data-testid="pos-delivery-address"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-[11px] text-brand-brown/60">Delivery area *</Label>
          <select
            value={d.form.delivery_area_id}
            onChange={(e) => d.setField("delivery_area_id", e.target.value)}
            className="w-full h-10 text-[13px] border border-[#E5E7EB] rounded-md px-3 bg-white font-medium"
            data-testid="pos-delivery-area"
            disabled={d.areasLoading}
          >
            <option value="">Select area</option>
            {d.activeAreas.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
                {Number(a.delivery_charge) > 0 ? ` (${money2(a.delivery_charge)})` : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-[11px] text-brand-brown/60">Driver</Label>
            {!d.assignedDriver && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onAutoAssign?.()}
                disabled={isAssigningDriver || d.driversLoading}
                data-testid="pos-delivery-auto-assign"
                className="h-8 text-[11px] font-bold border-violet-300 text-violet-900"
              >
                {isAssigningDriver ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <UserPlus size={12} className="mr-1" />
                )}
                {d.autoAssignEnabled ? "Auto assign" : "Assign best"}
              </Button>
            )}
          </div>
          {d.assignedDriver && (
            <p className="text-[12px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
              Assigned: {d.assignedDriver}
            </p>
          )}
          {!d.assignedDriver && d.suggestedDriver && (
            <p className="text-[11px] text-violet-800">
              Suggested: {d.suggestedDriver.name}
            </p>
          )}
          <select
            value={d.form.delivery_person_id}
            onChange={(e) => {
              const val = e.target.value;
              d.setField("delivery_person_id", val);
              if (val && onAssignDriver) {
                onAssignDriver(Number(val));
              }
            }}
            className="w-full h-10 text-[13px] border border-[#E5E7EB] rounded-md px-3 bg-white font-medium"
            data-testid="pos-delivery-driver"
            disabled={d.driversLoading || isAssigningDriver}
          >
            <option value="">Select driver</option>
            {d.drivers.map((driver) => (
              <option key={driver.id} value={driver.id}>
                {driver.suggested ? "★ " : ""}
                {driver.name}
                {driver.open_orders > 0 ? ` (${driver.open_orders} active)` : ""}
                {driver.availability !== "Available" ? ` · ${driver.availability}` : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="shrink-0 border-t border-[#F0E0D4] px-4 py-3 flex gap-2 justify-end bg-[#FFFCFA]">
        {onClose && (
          <Button type="button" variant="outline" className="h-10" onClick={onClose}>
            Cancel
          </Button>
        )}
        <Button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          data-testid="pos-delivery-save"
          className="h-10 bg-violet-700 hover:bg-violet-800 font-bold min-w-[120px]"
        >
          {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} className="mr-1.5" />}
          Save
        </Button>
      </div>
    </div>
  );
}
